// ── APM: must be the first import so EDOT Browser instruments fetch/XHR early ──
import "./lib/apm";
import "./lib/performance";
import React, { useEffect, useState } from "react";

import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import "./index.css";
import { router } from "./router";
import { DATA_SOURCE } from "./config/dataSource";
import { loadFestivalData, reloadFestivalData, subscribeToData } from "./services/dataService";

// ── Startup diagnostics & PWA Event Listeners ─────────────────────────────────
console.info(
  "%c[Tilos Maraton] App starting",
  "color: #ef5b5d; font-weight: bold; font-size: 14px",
);
console.info("[Tilos Maraton] Environment:", {
  DATA_SOURCE,
  BASE_NAME: process.env.BASE_NAME || "/",
  online: navigator.onLine,
  serviceWorkerSupported: "serviceWorker" in navigator,
});

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    console.info(`[Tilos Maraton] Service workers registered: ${regs.length}`);
    for (const reg of regs) {
      console.info(`  ↳ SW scope: ${reg.scope}, state: ${reg.active?.state ?? "pending"}`);
    }
  });

  // Listen for the controller changing (new Service Worker took control) and auto-reload the page
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    console.info("[Tilos Maraton] New service worker activated — reloading page...");
    window.location.reload();
  });
}

// Intercept HTML5 PWA installation prompt
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.deferredPrompt = e;
  console.debug("[Tilos Maraton] PWA is installable. Storing prompt.");
  window.dispatchEvent(new Event("pwa-installable"));
});
// ─────────────────────────────────────────────────────────────────────────────

const FestivalDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [, setTick] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Render the UI instantly using bundled / cached data.
    // Set loading to false in requestAnimationFrame to let maps and state components initialize.
    const frameId = requestAnimationFrame(() => {
      setLoading(false);
    });

    // Load festival data in the background (SWR pattern)
    loadFestivalData();

    // Reload on visibility change only (covers tab focus in most browsers).
    // Debounced to avoid double-firing when both visibilitychange and focus fire together.
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (debounceTimer) return;
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        console.debug("[Tilos Maraton] App became visible — reloading festival data");
        reloadFestivalData(true);
      }, 200);
    };
    document.addEventListener("visibilitychange", onVisible);

    // Subscribe to data changes to force re-render when remote data returns
    const unsubscribe = subscribeToData(() => {
      console.debug("[Tilos Maraton] Data store updated — triggering re-render");
      setTick((t) => t + 1);
    });

    return () => {
      cancelAnimationFrame(frameId);
      document.removeEventListener("visibilitychange", onVisible);
      if (debounceTimer) clearTimeout(debounceTimer);
      unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-main font-bold animate-pulse text-xl">Betöltés...</div>
      </div>
    );
  }

  return <>{children}</>;
};

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Failed to find the root element");
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <FestivalDataProvider>
      <RouterProvider router={router} />
    </FestivalDataProvider>
  </React.StrictMode>,
);
